import { Suspense, useEffect, useLayoutEffect, useState } from "react";
import Image from "next/image";
import dynamic from "next/dynamic";
import {
  Box,
  CardActionArea,
  CardHeader,
  Grid,
  Stack,
  Typography,
  useMediaQuery
} from "@mui/material";
import {
  RssFeedRounded as FeedIcon,
  CheckBoxRounded as BulletPointIcon
} from "@mui/icons-material";

import { useStore, observer } from "../../../../../service/mobx";
import { SubHeader as ModelSubheader } from "../../../../../component/Card/Model";
import { SubHeader as PaperSubheader } from "../../../../../component/Card/Paper";
import ButtonFilled from "../../../../../component/Button/Filled";

const Masonry = dynamic(
  () => import("masonic").then(module => module.Masonry),
  { ssr: false }
);

function Feed({ trending: _trending }) {
  const [masonryKey, setMasonryKey] = useState(true);
  const { device } = useStore();
  const [trending, setTrending] = useState(_trending);
  const isSuperXL = useMediaQuery("(min-width:2000px)");

  useEffect(() => {
    if (isSuperXL) {
      setTrending(_trending.slice(0, 15));
      return;
    }

    switch (device.size) {
      case "compact": {
        setTrending(_trending);
        break;
      }
      case "medium": {
        setTrending(_trending);
        break;
      }
      case "expanded": {
        setTrending(_trending.slice(0, 6));
        break;
      }
      case "large": {
        setTrending(_trending.slice(0, 9));
        break;
      }
      case "extraLarge": {
        setTrending(_trending.slice(0, 12));
        break;
      }
    }
  }, [_trending, device.isPhone, device.size, isSuperXL]);

  useLayoutEffect(() => {
    const onResize = () => setMasonryKey(key => !key);

    window.addEventListener("resize", onResize);

    onResize();

    return () => window.removeEventListener("resize", onResize);
  }, [device.size, isSuperXL]);

  return (
    <Grid
      container
      columns={100}
      spacing={6}
      height="inherit"
      position="relative"
      pl={{ compact: 1, expanded: 0 }}
      pr={{ compact: 0, large: 10 }}
    >
      <Grid
        size={{ compact: 100, expanded: 38 }}
        height={{ compact: "min-content", expanded: "auto" }}
      >
        <TextArea />
      </Grid>
      <Grid
        position="relative"
        size={{ compact: 50, expanded: 62 }}
        px={{ compact: 0, expanded: 3 }}
        sx={theme => ({
          ...(device.isPhone ? {} : theme.fadeEdges(["top", "bottom"], 95))
        })}
      >
        {device.isPhone ? (
          <Stack
            pl={4}
            pr={8}
            spacing={1}
            width="100vw"
            direction="row"
            sx={{
              overflowY: "hidden",
              scrollbarWidth: "none", // Firefox
              "&::-webkit-scrollbar": { display: "none" } // Chrome/Safari
            }}
          >
            {trending.map(entity => (
              <Box key={entity.id} minWidth={200}>
                <Card entity={entity} />
              </Box>
            ))}
          </Stack>
        ) : (
          <Suspense fallback={<Box height="100hv" />}>
            <Masonry
              key={masonryKey}
              maxColumnCount={5}
              columnGutter={8}
              items={trending}
              render={render}
              overscanBy={Infinity}
            />
          </Suspense>
        )}
      </Grid>
    </Grid>
  );
}
export default observer(Feed);

const render = ({ data }) => <Card key={data.id} entity={data} />;

function Card({ entity }) {
  const [imageSrc, setImageSrc] = useState(
    [entity.imgTask, entity.imgPaper, entity.repo?.hfOwner?.avatar].filter(
      defined => defined
    )
  );

  return (
    <CardActionArea
      href={
        entity.isPaper
          ? `/docs/${entity.publisher}/${entity.paperID}`
          : `/model/${entity.org}/${entity.displayName}`
      }
    >
      <Stack
        spacing={2}
        overflow="hidden"
        boxShadow="var(--elevation-3)"
        borderRadius="var(--shape-md-round)"
        sx={{
          textDecoration: "none",
          bgcolor: "var(--surface-container-color)",
          transition: "all 500ms var(--motion-easing-emphasized)",
          "&:hover": {
            cursor: "pointer",
            boxShadow: "var(--elevation-5)",
            bgcolor: "var(--primary-container)",
            borderColor: "var(--primary-color)"
          },
          "&:hover p": {
            color: "var(--primary-on-container)"
          },
          "&:hover .brand": {
            color: "var(--dark-primary-on-container)"
          },
          "&:hover img": {
            filter: "unset"
          }
        }}
      >
        {imageSrc.length ? (
          <Box
            sx={{
              position: "relative",
              height: 160,
              borderRadius: "var(--shape-md-top)",
              overflow: "hidden",
              "& img": {
                objectFit: "cover",
                filter: "grayscale(62%)",
                mixBlendMode: "plus-darker",
                transition: "all 500ms var(--motion-easing-emphasized)"
              }
            }}
          >
            <Image
              fill
              src={imageSrc[0]}
              alt={entity.title || entity.name || "Card image"}
              sizes="(max-width: 768px) 200px, (max-width: 1200px) 300px, 400px"
              onError={() => {
                const [, ...remainingImages] = imageSrc;

                setImageSrc(remainingImages);
              }}
            />
          </Box>
        ) : (
          <Typography
            className="brand"
            height={160}
            width="100%"
            display="flex"
            alignItems="center"
            justifyContent="center"
            typography="brand"
            translate="no"
            fontSize={64}
            bgcolor="var(--dark-surface-dim)"
            borderRadius="var(--shape-md-top)"
            color="var(--dark-surface-on-color)"
          >
            b
          </Typography>
        )}
        <Box p={2} pt={0}>
          <CardHeader
            title={entity.title || entity.displayName}
            sx={{
              p: 0,
              pb: 1,
              "& p": {
                transition: "all 500ms var(--motion-easing-emphasized)"
              }
            }}
            slotProps={{
              title: {
                fontWeight: 500,
                sx: { wordBreak: "break-word" },
                typography: { compact: "titleSm", expanded: "titleMd" }
              }
            }}
          />
          <Box
            color="var(--secondary-color)"
            typography={{ compact: "bodySm", expanded: "bodyMd" }}
          >
            {entity.isPaper ? (
              <PaperSubheader org publisher paper={entity} />
            ) : (
              <ModelSubheader
                downloads
                likes
                org
                params
                task
                model={entity}
                published={false}
              />
            )}
          </Box>
        </Box>
      </Stack>
    </CardActionArea>
  );
}
const TextArea = () => (
  <Stack
    px={{ compact: 2, expanded: 3 }}
    spacing={3}
    alignItems="center"
    justifyContent="center"
  >
    <Stack alignItems="center">
      <FeedIcon
        sx={{
          color: "var(--primary-color)",
          height: { compact: 64, expanded: 80 },
          width: { compact: 64, expanded: 80 }
        }}
      />
      <Typography
        fontWeight="700 !important"
        color="var(--secondary-color)"
        typography={{ compact: "headlineSm", expanded: "displayMd" }}
      >
        Feed
      </Typography>
    </Stack>
    <Typography
      align="center"
      sx={{ textWrap: "balance" }}
      fontWeight="700 !important"
      typography={{ compact: "headlineLg", expanded: "displayLg" }}
    >
      AI moves fast
      <br /> Stay faster
    </Typography>
    <Typography
      align="center"
      sx={{ textWrap: "balance" }}
      typography={{ compact: "titleLg", expanded: "headlineSm" }}
    >
      Bytez tracks every new model & paper, then delivers the few that matter to
      you — every day
    </Typography>
    <Stack
      spacing={4}
      alignItems="center"
      borderRadius="var(--shape-sm)"
      boxShadow="var(--elevation-1)"
      sx={{ p: 2, bgcolor: "var(--surface-container-color)" }}
    >
      <Stack
        component="ul"
        spacing={1.5}
        sx={{ m: 0, p: 0, listStyle: "none" }}
      >
        {[
          "Spot breakthroughs the day they drop",
          "See trends before the trend-line",
          "Cut the noise, keep the edge"
        ].map(text => (
          <Typography
            component="li"
            typography={{ compact: "bodyMd", expanded: "bodyLg" }}
            key={text}
            sx={{
              display: "flex",
              alignItems: "flex-start",
              gap: 1.5
            }}
          >
            <BulletPointIcon
              fontSize="small"
              sx={{ mt: "2px", color: "var(--primary-color)" }}
            />
            {text}
          </Typography>
        ))}
      </Stack>
      <ButtonFilled
        label="Create your feed"
        href="/feed"
        sx={{
          borderRadius: "var(--shape-sm)"
        }}
      />
    </Stack>
  </Stack>
);
